import { useMemo } from 'react';
import type { FetchOptions } from '@atlassian/jira-insights-common-constants/src/common/types/fetch.tsx';
import { createStore, createHook, type Store } from '@atlassian/react-sweet-state';
import type {
	CreateFetcherProps,
	HookEffects,
	CreateFetcherReturn,
	CreateFetchHookCreated,
	SweetState,
	UseFetchStateProps,
	BuildSweetStateActionDispatchersProps,
} from '../types.tsx';
import {
	buildSweetStateActionDispatchers,
	hydrateSweetStateActionsDispatchers,
} from './action-dispatchers.tsx';
import { buildHook } from './hook-builder.tsx';
import { initialState } from './state.tsx';

const initialStoreState = {
	fetchables: {},
} as const;

const actions = buildSweetStateActionDispatchers();

export const createFetchStore = ({ name }: { name: string }) =>
	createStore<SweetState, BuildSweetStateActionDispatchersProps>({
		initialState: initialStoreState,
		actions,
		name: `use-fetch-store:${name}`,
	});

export const createFetcher = (config: CreateFetcherProps): CreateFetcherReturn => {
	if (config.getProps === undefined) {
		return {
			key: config.getKey,
			fetch: config.getData,
			props: () => ({}),
		};
	}

	return {
		key: config.getKey,
		fetch: config.getData,
		props: config.getProps,
	};
};

export const createFetchHook = (
	fetcher: CreateFetcherReturn,
	FetchStore: Store<SweetState, BuildSweetStateActionDispatchersProps>,
) => {
	const useFetchSweetState = createHook(FetchStore, {
		selector: (
			state: SweetState,
			{
				key,
			}: {
				key: string;
			},
		) => (key && state.fetchables[key]) || initialState,
	});

	const useFetchState = ({ initialData, key = 'static' }: UseFetchStateProps) => {
		const [state, sweetStateActions] = useFetchSweetState({ key });

		const memoActions = useMemo(
			() =>
				hydrateSweetStateActionsDispatchers({
					// @ts-expect-error - TS2322 - Type 'BoundActions<SweetState, BuildSweetStateActionDispatchersProps>' is not assignable to type 'BuildSweetStateActionDispatchersProps'.
					actions: sweetStateActions,
					key,
					initialData,
				}),
			[sweetStateActions, initialData, key],
		);

		return [state, memoActions] as const;
	};

	// @ts-expect-error - TS2322 - Type '({ initialData, key }: UseFetchStateProps) => readonly [any, BuildActionDispatchersProps]' is not assignable to type 'UseFetchReducer'.
	const dynamicHook = buildHook({ useFetchState });

	return ({ getParams }: CreateFetchHookCreated = {}, effects: HookEffects) => {
		const params = getParams?.();

		const { onFetch, key, props } = useMemo(() => {
			const sliceKey = fetcher.key(params);
			const fetch = (signal: FetchOptions) => fetcher.fetch(signal, params);
			const propsHook = fetcher.props(params);
			return { onFetch: fetch, key: sliceKey, props: propsHook };
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, effects);

		return dynamicHook({ onFetch, key, ...props }, effects);
	};
};
